export type FormImage = {
    url: string;
    name?: string;
};

function parseFromString(value: string): FormImage[] {
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            return parsed
                .map((item) => {
                    if (typeof item === "string") {
                        return { url: item } as FormImage;
                    }
                    if (item && typeof item === "object" && typeof item.url === "string") {
                        return {
                            url: item.url,
                            name: typeof item.name === "string" ? item.name : "",
                        } as FormImage;
                    }
                    return null;
                })
                .filter((item): item is FormImage => !!item);
        }
    } catch {
        // If parsing fails, treat the value as a single URL.
    }
    if (value.startsWith("http://") || value.startsWith("https://")) {
        return [{ url: value }];
    }
    return [];
}

export function parseFormImages(value: unknown): FormImage[] {
    if (!value && value !== 0) {
        return [];
    }
    if (typeof value === "string") {
        return parseFromString(value);
    }
    if (Array.isArray(value)) {
        return value
            .map((item) => {
                if (typeof item === "string") {
                    return { url: item } as FormImage;
                }
                if (item && typeof item === "object" && typeof (item as FormImage).url === "string") {
                    const cast = item as FormImage;
                    return { url: cast.url, name: cast.name ?? "" } as FormImage;
                }
                return null;
            })
            .filter((item): item is FormImage => !!item);
    }
    return [];
}

export function stringifyFormImages(images: FormImage[]): string {
    return JSON.stringify(images.map((image) => ({ url: image.url, name: image.name ?? "" })));
}

export function addFormImage(currentValue: unknown, url: string, name = ""): string {
    const images = parseFormImages(currentValue);
    images.push({ url, name });
    return stringifyFormImages(images);
}

export function removeFormImage(currentValue: unknown, url: string): string {
    const images = parseFormImages(currentValue).filter((image) => image.url !== url);
    return stringifyFormImages(images);
}

export function hasFormImages(value: unknown): boolean {
    return parseFormImages(value).length > 0;
}
